
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { Router, Timer, Cpu, Thermometer } from 'lucide-react';

interface GatewayMetricsProps {
  device: any;
}

const GatewayMetrics: React.FC<GatewayMetricsProps> = ({ device }) => {
  return (
    <>
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium">Connected Devices</p>
              <h3 className="text-2xl font-bold mt-1">{device.connectedDevices}</h3>
            </div>
            <div className="rounded-full bg-blue-100 p-2 dark:bg-blue-900/30">
              <Router className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium">Uptime</p>
              <h3 className="text-2xl font-bold mt-1">{Math.floor(device.uptimeHours / 24)}d {device.uptimeHours % 24}h</h3>
            </div>
            <div className="rounded-full bg-green-100 p-2 dark:bg-green-900/30">
              <Timer className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium">CPU / Memory</p>
              <h3 className="text-2xl font-bold mt-1">{device.cpuUsage}% / {device.memoryUsage}%</h3>
            </div>
            <div className={cn(
              "rounded-full p-2",
              device.cpuUsage > 80 || device.memoryUsage > 80 ? "bg-red-100 dark:bg-red-900/30" :
              device.cpuUsage > 60 || device.memoryUsage > 60 ? "bg-amber-100 dark:bg-amber-900/30" : "bg-green-100 dark:bg-green-900/30"
            )}>
              <Cpu className={cn(
                "h-5 w-5",
                device.cpuUsage > 80 || device.memoryUsage > 80 ? "text-red-600 dark:text-red-400" :
                device.cpuUsage > 60 || device.memoryUsage > 60 ? "text-amber-600 dark:text-amber-400" : "text-green-600 dark:text-green-400"
              )} />
            </div>
          </div>
          <div className="space-y-2 mt-2">
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span>CPU</span>
                <span>{device.cpuUsage}%</span>
              </div>
              <Progress value={device.cpuUsage} className="h-1.5" />
            </div>
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span>Memory</span>
                <span>{device.memoryUsage}%</span>
              </div>
              <Progress value={device.memoryUsage} className="h-1.5" />
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium">Temperature</p>
              <h3 className="text-2xl font-bold mt-1">{device.temperatureCelsius}°C</h3>
            </div>
            <div className={cn(
              "rounded-full p-2",
              device.temperatureCelsius > 60 ? "bg-red-100 dark:bg-red-900/30" :
              device.temperatureCelsius > 45 ? "bg-amber-100 dark:bg-amber-900/30" : "bg-green-100 dark:bg-green-900/30"
            )}>
              <Thermometer className={cn(
                "h-5 w-5",
                device.temperatureCelsius > 60 ? "text-red-600 dark:text-red-400" :
                device.temperatureCelsius > 45 ? "text-amber-600 dark:text-amber-400" : "text-green-600 dark:text-green-400"
              )} />
            </div>
          </div>
          <Progress 
            value={(device.temperatureCelsius / 100) * 100} 
            className="h-2 mt-4" 
          />
        </CardContent>
      </Card>
    </>
  );
};

export default GatewayMetrics;
